// Overlay full-screen para el step `configure` cuando el preset es
// `catalogo-ia`. Muestra una card por cada (vista × producto)
// compatible — el user puede remover combos individuales antes de
// generar. Cada card replica el layout de los slots multi-modelo:
// imagen de la vista a la izquierda, nombre + thumb del producto a
// la derecha, X en la esquina para descartar la combinación.
//
// Filtrado de compatibilidad (helper `isVistaCompatibleConModelView`):
//   • con-modelo (poses) → se combina solo con productos cuyo `modelView`
//                          está en `areasPrincipales` de la pose (los
//                          tipos de producto sugeridos). Una pose que
//                          enfoca la cabeza no va con un zapato.
//   • solo-producto      → se combina solo con productos cuyo `modelView`
//                          matchea `compatibleModelViews` de la vista.

import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Image as ImageIcon, Shirt, Sparkles, UserRound, X } from 'lucide-react';
import type { MockProductV2 } from '../mockData';
import {
  isVistaCompatibleConModelView,
  resolveVistaImageUrl,
  type VistaCatalogo,
} from '../vistasCatalogo';
import { MultiSlotProductPickerSheet } from './MultiSlotProductPickerSheet';
import { AnimatedBlurBg } from '../AnimatedBlurBg';
import type { FondoSelection } from '../FondoPicker';
import type { ModelSelection } from '../ModelPicker';

export interface CatalogoCard {
  vista: VistaCatalogo;
  product: MockProductV2;
  /** Clave única para identificar la combinación. */
  key: string;
  /** Productos a combinar en esta imagen, ORDENADOS — el primero es el
   *  principal (p. ej. [remera, pantalón]). Viajan en `variante_id`, igual
   *  que el modo multi-producto de modelo. */
  comboProducts?: MockProductV2[];
}

export interface CatalogoCardsFullscreenProps {
  open: boolean;
  variant: 'mobile' | 'desktop';
  sidebarWidth: number;
  selectedProducts: MockProductV2[];
  selectedVistas: VistaCatalogo[];
  onBack: () => void;
  onGenerate: (cards: CatalogoCard[]) => void;
  // ── Props para el sheet de productos secundarios (combinar / outfit) ──
  userCompanyId: string | null;
  brandId: string | null;
  rubro: string | null;
  tiposProductoSugeridos?: string[] | null;
  onProductsRefreshed: () => void;
  // ── Fondo + Modelo globales de la tanda ──────────────────────────────
  // Selecciones que aplican a TODAS las generaciones del catálogo. Los
  // botones abren los pickers existentes (mismos del modo modelo).
  fondoSelection: FondoSelection | null;
  modelSelection: ModelSelection | null;
  onOpenFondo: () => void;
  onOpenModel: () => void;
}

export const CatalogoCardsFullscreen: React.FC<CatalogoCardsFullscreenProps> = ({
  open,
  variant,
  sidebarWidth,
  selectedProducts,
  selectedVistas,
  onBack,
  onGenerate,
  userCompanyId,
  brandId,
  rubro,
  tiposProductoSugeridos,
  onProductsRefreshed,
  fondoSelection,
  modelSelection,
  onOpenFondo,
  onOpenModel,
}) => {
  // Animación de entrada — misma que VistaPickerFullscreen.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Conjunto inicial de cards (vista × producto compatible). Se
  // recalcula cuando cambian las selecciones. `removedKeys` lleva las
  // que el user descartó manualmente con la X.
  const allCards = React.useMemo<CatalogoCard[]>(() => {
    const cards: CatalogoCard[] = [];
    for (const vista of selectedVistas) {
      for (const product of selectedProducts) {
        // Tanto poses (con-modelo) como vistas solo-producto se combinan
        // SOLO con los productos compatibles: una pose que enfoca los pies
        // va con el zapato, no con la remera.
        if (isVistaCompatibleConModelView(vista, product.modelView)) {
          cards.push({
            vista,
            product,
            key: `${vista.kind}-${vista.id}__${product.id}`,
          });
        }
      }
    }
    return cards;
  }, [selectedVistas, selectedProducts]);
  const [removedKeys, setRemovedKeys] = useState<Set<string>>(new Set());
  // Al cambiar las selecciones, limpiamos las descartadas que ya no
  // existen en `allCards` (sino se acumulan).
  useEffect(() => {
    setRemovedKeys((prev) => {
      const validKeys = new Set(allCards.map((c) => c.key));
      const next = new Set<string>();
      for (const k of prev) if (validKeys.has(k)) next.add(k);
      return next;
    });
  }, [allCards]);
  const activeCards = React.useMemo(
    () => allCards.filter((c) => !removedKeys.has(c.key)),
    [allCards, removedKeys],
  );

  const removeCard = (key: string) =>
    setRemovedKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });

  // Agrupamos las cards activas POR PRODUCTO (en el orden en que el user
  // seleccionó los productos), para que las vistas de un mismo producto
  // queden juntas en vez de intercaladas. Solo entran productos que
  // todavía tienen al menos una vista activa.
  const groupedByProduct = React.useMemo(
    () =>
      selectedProducts
        .map((product) => ({
          product,
          cards: activeCards.filter((c) => c.product.id === product.id),
        }))
        .filter((g) => g.cards.length > 0),
    [selectedProducts, activeCards],
  );

  // ── Productos combinados (outfit) ───────────────────────────────────
  // Cada card combina uno o varios productos en una sola imagen. La lista
  // va ORDENADA: el primero es el PRINCIPAL. `productCombos` lo setea
  // "Editar outfit" (aplica a todas las poses del producto); `cardCombos`
  // pisa una card puntual. Combo efectivo = card ?? producto ?? [principal].
  const [productCombos, setProductCombos] = useState<
    Record<string, MockProductV2[]>
  >({});
  const [cardCombos, setCardCombos] = useState<
    Record<string, MockProductV2[]>
  >({});
  // Qué estamos editando cuando el sheet está abierto.
  const [sheetTarget, setSheetTarget] = useState<
    | { type: 'card'; key: string; productId: string }
    | { type: 'product'; productId: string }
    | null
  >(null);

  const effectiveCombo = (card: CatalogoCard): MockProductV2[] =>
    cardCombos[card.key] ?? productCombos[card.product.id] ?? [card.product];

  // Selección inicial del sheet según el target (combo actual, principal
  // primero).
  const sheetSelection: MockProductV2[] = (() => {
    if (!sheetTarget) return [];
    if (sheetTarget.type === 'card') {
      const card = activeCards.find((c) => c.key === sheetTarget.key);
      return card ? effectiveCombo(card) : [];
    }
    const product = selectedProducts.find(
      (p) => p.id === sheetTarget.productId,
    );
    return productCombos[sheetTarget.productId] ?? (product ? [product] : []);
  })();

  const handleSheetConfirm = (products: MockProductV2[]) => {
    if (!sheetTarget) return;
    if (sheetTarget.type === 'product') {
      const pid = sheetTarget.productId;
      setProductCombos((prev) => ({ ...prev, [pid]: products }));
      // "Editar outfit" es autoritativo para todas las poses del producto:
      // limpiamos overrides por card de ese producto (su key termina en
      // `__<productId>`) para que el outfit aplique parejo.
      setCardCombos((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          if (k.endsWith(`__${pid}`)) delete next[k];
        }
        return next;
      });
    } else {
      const key = sheetTarget.key;
      setCardCombos((prev) => ({ ...prev, [key]: products }));
    }
    setSheetTarget(null);
  };

  // Adjunta el combo efectivo (principal + combinados) a cada card antes de
  // generar — el padre lo manda en `variante_id`, igual que multi-producto
  // del modo modelo.
  const handleGenerate = () =>
    onGenerate(
      activeCards.map((c) => ({ ...c, comboProducts: effectiveCombo(c) })),
    );

  // Labels de los botones globales Fondo / Modelo (default blanco / IA).
  const fondoLabel = (() => {
    const s = fondoSelection;
    if (!s || s.mode === 'blanco') return 'Blanco';
    if (s.mode === 'auto') return 'Automático';
    if (s.mode === 'biblioteca' || s.mode === 'guardado') return s.name;
    return 'Personalizado';
  })();
  const modeloLabel = (() => {
    const s = modelSelection;
    if (!s || s.mode === 'auto') return 'Automático';
    if (s.mode === 'preset') return s.name;
    if (s.mode === 'custom') return 'Personalizado';
    return 'Subido';
  })();
  // El botón de Modelo solo aplica si hay poses con-modelo en la tanda.
  const hasModelo = activeCards.some((c) => c.vista.kind === 'con-modelo');

  // Datos para el thumbnail de las cards Fondo/Modelo en desktop.
  const fondoIsAuto = fondoSelection?.mode === 'auto';
  const fondoIsBlanco = !fondoSelection || fondoSelection.mode === 'blanco';
  const fondoImg =
    fondoSelection?.mode === 'biblioteca' ||
    fondoSelection?.mode === 'guardado'
      ? fondoSelection.imageUrl
      : null;
  const modeloIsAuto = !modelSelection || modelSelection.mode === 'auto';
  const modeloImg =
    modelSelection?.mode === 'preset'
      ? modelSelection.imageUrl
      : modelSelection?.mode === 'upload'
        ? modelSelection.previewUrl
        : null;

  const isMobile = variant === 'mobile';

  if (!open) return null;

  // Separador de grupo — thumb + nombre del producto, ocupando toda la
  // fila del grid. Es lo que hace visible la clasificación por producto,
  // igual que el catálogo de GeneratedImagesInline.
  const renderProductHeader = (product: MockProductV2) => {
    // Combinados extra del producto (combo menos el principal).
    const extra = Math.max(0, (productCombos[product.id]?.length ?? 1) - 1);
    return (
      <div
        key={`header-${product.id}`}
        className="col-span-full flex items-center gap-2.5 pt-3 pb-1 first:pt-0"
      >
        <img
          src={product.imageUrl}
          alt={product.name}
          className="w-10 h-10 rounded-lg object-cover ring-1 ring-black/[0.06] shrink-0"
        />
        <span className="min-w-0 max-w-max text-[15px] font-semibold text-brand-black/90 truncate">
          {product.name}
        </span>
        {/* "Editar outfit" — justo a la derecha del título. Productos
            secundarios para TODAS las poses de este producto. */}
        <button
          type="button"
          onClick={() => setSheetTarget({ type: 'product', productId: product.id })}
          className="ml-auto sm:ml-0 shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-neutral-200 bg-white hover:bg-neutral-50 active:scale-95 text-[12.5px] font-medium text-brand-black/80 transition-all"
        >
          <Shirt className="w-3.5 h-3.5" />
          <span>Editar outfit</span>
          {extra > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand-primary text-white text-[11px] font-semibold">
              +{extra}
            </span>
          )}
        </button>
      </div>
    );
  };

  const renderCard = (card: CatalogoCard) => (
    <div
      key={card.key}
      className="relative rounded-2xl border border-neutral-200 bg-white overflow-hidden animate-mode-swap"
    >
      {/* X — esquina superior derecha, igual al patrón MultiSlot. */}
      <button
        type="button"
        onClick={() => removeCard(card.key)}
        aria-label={`Quitar ${card.vista.nombre} para ${card.product.name}`}
        className="absolute top-2.5 right-2.5 z-10 w-7 h-7 inline-flex items-center justify-center rounded-full bg-black/55 backdrop-blur-sm text-white hover:bg-black/70 active:scale-95 transition-all"
      >
        <X className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
      <div className="flex items-stretch gap-3 p-3">
        {/* Imagen de la vista (mitad izquierda). Resolvemos con el
            model_view del producto de ESTA card específica, así cada
            (vista × producto) muestra el thumb correcto cuando hay un
            override (ropa). */}
        <div className="shrink-0 w-1/2 aspect-[3/4] rounded-lg overflow-hidden bg-neutral-100">
          {(() => {
            const thumb = resolveVistaImageUrl(
              card.vista,
              card.product.modelView,
            );
            return thumb ? (
              <img
                src={thumb}
                alt={card.vista.nombre}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#3C3C43]/40 text-[11px] text-center px-2">
                {card.vista.nombre}
              </div>
            );
          })()}
        </div>
        {/* Info derecha: tipo + nombre vista + producto + thumb. */}
        <div className="flex-1 min-w-0 flex flex-col items-center justify-center gap-3 text-center">
          <div className="min-w-0 w-full">
            <p className="text-[10.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
              {card.vista.kind === 'con-modelo' ? 'Pose' : 'Vista'}
            </p>
            {/* `line-clamp-2` parte el nombre largo en máximo 2 líneas
                y agrega ellipsis al final si todavía no cabe. */}
            <p
              className="text-[15px] font-semibold leading-snug text-brand-black/90 line-clamp-2 break-words"
              title={card.vista.nombre}
            >
              {card.vista.nombre}
            </p>
          </div>
          {/* Bloque de producto(s) tappable: abre el sheet para combinar
              productos. Cuando hay varios, se muestran STACKEADOS (mismo
              patrón que el chip de productos seleccionados en config). */}
          {(() => {
            const combo = effectiveCombo(card);
            const multiple = combo.length > 1;
            return (
              <button
                type="button"
                onClick={() =>
                  setSheetTarget({
                    type: 'card',
                    key: card.key,
                    productId: card.product.id,
                  })
                }
                className="flex flex-col items-center gap-1.5 rounded-lg px-1 py-1 -my-1 max-w-full hover:bg-black/[0.03] active:scale-[0.98] transition-all"
              >
                <p className="text-[10.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
                  {multiple ? 'Productos' : 'Producto'}
                </p>
                {multiple ? (
                  <div className="flex items-center -space-x-2">
                    {combo.slice(0, 3).map((p) => (
                      <img
                        key={p.id}
                        src={p.imageUrl}
                        alt={p.name}
                        className="w-10 h-10 rounded-lg object-cover ring-2 ring-white bg-neutral-100"
                      />
                    ))}
                    {combo.length > 3 && (
                      <div className="w-10 h-10 rounded-lg bg-neutral-100 ring-2 ring-white inline-flex items-center justify-center text-[11px] font-semibold text-brand-black/70">
                        +{combo.length - 3}
                      </div>
                    )}
                  </div>
                ) : (
                  <img
                    src={combo[0].imageUrl}
                    alt={combo[0].name}
                    className="w-12 h-12 rounded-lg object-cover ring-1 ring-black/[0.06]"
                  />
                )}
                <p className="text-[12.5px] text-brand-black/80 leading-tight max-w-full truncate px-1">
                  {multiple
                    ? `${combo[0].name} y ${combo.length - 1} más`
                    : combo[0].name}
                </p>
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[110] bg-white flex flex-col transition-[opacity,transform] duration-200 ease-out"
      style={{
        left: isMobile ? 0 : sidebarWidth,
        right: 0,
        top: 0,
        opacity: entered ? 1 : 0,
        transform: entered ? 'translateY(0)' : 'translateY(8px)',
      }}
    >
      {/* Cobertura blanca sobre la zona del chrome flotante mobile. */}
      {isMobile && (
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 bg-white pointer-events-none z-[5]"
          style={{
            height: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
          }}
        />
      )}

      {/* Desktop sticky top bar: Volver (left) + Generar (right). */}
      {!isMobile && (
        <div className="shrink-0 z-[20] px-8 pt-5 pb-4 flex items-center justify-between bg-white">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={activeCards.length === 0}
            className="inline-flex items-center gap-2 py-2.5 pl-5 pr-2.5 rounded-full bg-brand-primary disabled:opacity-40 disabled:cursor-not-allowed hover:bg-brand-primary-hover text-white text-[14.5px] font-semibold shadow-md transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            <span>Generar</span>
            {activeCards.length > 0 && (
              <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold ml-1">
                <img
                  src="/images/token_icon.png"
                  alt=""
                  aria-hidden="true"
                  className="w-3.5 h-3.5 object-contain"
                />
                {activeCards.length}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Scroll area */}
      <div className="flex-1 overflow-y-auto">
        {/* Title */}
        <div
          className="px-5 sm:px-8 pb-3 sm:pt-2"
          style={{
            paddingTop: isMobile
              ? 'calc(env(safe-area-inset-top, 0px) + 3.5rem + 1rem)'
              : undefined,
          }}
        >
          <h1 className="text-[22px] sm:text-[26px] font-semibold text-brand-black/90 leading-tight">
            Revisá tu catálogo
          </h1>
          <p className="text-[13.5px] sm:text-[14.5px] text-[#3C3C43]/60 mt-1">
            {activeCards.length === 0
              ? 'No quedan combinaciones para generar — agregá vistas o productos.'
              : `Vamos a generar ${activeCards.length} ${
                  activeCards.length === 1 ? 'imagen' : 'imágenes'
                }. Tocá la X de cualquier card para descartarla.`}
          </p>
          {/* Fondo + Modelo globales: aplican a TODA la tanda. El botón de
              Modelo solo aparece si hay alguna pose con-modelo (las vistas
              solo-producto no usan modelo). */}
          {/* Mobile: filas compactas (label izq · valor der · chevron). */}
          {activeCards.length > 0 && (
            <div className="sm:hidden flex flex-col gap-2.5 mt-4">
              <button
                type="button"
                onClick={onOpenFondo}
                className="w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border border-neutral-200 bg-white active:scale-[0.98] transition-all"
              >
                <span className="inline-flex items-center gap-2 text-[14px] text-brand-black shrink-0">
                  <ImageIcon className="w-[18px] h-[18px]" />
                  Fondo
                </span>
                <span className="inline-flex items-center gap-1.5 min-w-0">
                  <span className="text-[14px] font-semibold text-brand-black truncate">
                    {fondoLabel}
                  </span>
                  <ChevronRight className="w-4 h-4 text-[#3C3C43]/40 shrink-0" />
                </span>
              </button>
              {hasModelo && (
                <button
                  type="button"
                  onClick={onOpenModel}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border border-neutral-200 bg-white active:scale-[0.98] transition-all"
                >
                  <span className="inline-flex items-center gap-2 text-[14px] text-brand-black shrink-0">
                    <UserRound className="w-[18px] h-[18px]" />
                    Modelo
                  </span>
                  <span className="inline-flex items-center gap-1.5 min-w-0">
                    <span className="text-[14px] font-semibold text-brand-black truncate">
                      {modeloLabel}
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#3C3C43]/40 shrink-0" />
                  </span>
                </button>
              )}
            </div>
          )}

          {/* Desktop: cards del mismo ancho que las de vista (grid igual),
              horizontales (thumbnail cuadrado a la izquierda + label). El
              caso "automático" se anima igual que el tile del picker. */}
          {activeCards.length > 0 && (
            <div className="hidden sm:grid grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
              <button
                type="button"
                onClick={onOpenFondo}
                className="flex items-center gap-3 p-3 rounded-2xl border border-neutral-200 bg-white text-left hover:border-brand-primary/40 hover:shadow-sm active:scale-[0.99] transition-all"
              >
                <div className="w-24 h-24 shrink-0 rounded-xl overflow-hidden relative bg-neutral-100">
                  {fondoIsAuto ? (
                    <div className="absolute inset-0 bg-brand-primary flex items-center justify-center">
                      <AnimatedBlurBg />
                      <ImageIcon className="relative z-10 w-7 h-7 text-white drop-shadow" />
                    </div>
                  ) : fondoImg ? (
                    <img
                      src={fondoImg}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : fondoIsBlanco ? (
                    <div className="absolute inset-0 bg-white flex items-center justify-center">
                      <div className="w-10 h-10 rounded-md border-2 border-neutral-300 bg-white" />
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-[#3C3C43]/40">
                      <ImageIcon className="w-7 h-7" />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-[10.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
                    Fondo
                  </p>
                  <p className="text-[14px] font-semibold text-brand-black truncate mt-0.5">
                    {fondoLabel}
                  </p>
                </div>
              </button>
              {hasModelo && (
                <button
                  type="button"
                  onClick={onOpenModel}
                  className="flex items-center gap-3 p-3 rounded-2xl border border-neutral-200 bg-white text-left hover:border-brand-primary/40 hover:shadow-sm active:scale-[0.99] transition-all"
                >
                  <div className="w-24 h-24 shrink-0 rounded-xl overflow-hidden relative bg-neutral-100">
                    {modeloIsAuto ? (
                      <div className="absolute inset-0 bg-brand-primary flex items-center justify-center">
                        <AnimatedBlurBg />
                        <UserRound className="relative z-10 w-7 h-7 text-white drop-shadow" />
                      </div>
                    ) : modeloImg ? (
                      <img
                        src={modeloImg}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-[#3C3C43]/40">
                        <UserRound className="w-7 h-7" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
                      Modelo
                    </p>
                    <p className="text-[14px] font-semibold text-brand-black truncate mt-0.5">
                      {modeloLabel}
                    </p>
                  </div>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Grid de cards. Las cards mantienen su diseño de siempre — lo que
            agregamos es la clasificación por producto: cada grupo abre con
            un header (col-span-full) y debajo van sus cards. */}
        <div className="px-5 sm:px-8 py-5 sm:pb-20">
          {activeCards.length === 0 ? null : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {groupedByProduct.map(({ product, cards }) => (
                <React.Fragment key={product.id}>
                  {renderProductHeader(product)}
                  {cards.map(renderCard)}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile footer: botón Generar full-width. */}
      {isMobile && (
        <div
          className="shrink-0 border-t border-black/[0.06] bg-white px-5 pt-3 pb-4"
          style={{
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          }}
        >
          <button
            type="button"
            onClick={handleGenerate}
            disabled={activeCards.length === 0}
            className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-2xl bg-brand-primary disabled:opacity-40 disabled:cursor-not-allowed hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold transition-colors"
          >
            <Sparkles className="w-5 h-5" />
            <span>Generar</span>
            {activeCards.length > 0 && (
              <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold">
                <img
                  src="/images/token_icon.png"
                  alt=""
                  aria-hidden="true"
                  className="w-3.5 h-3.5 object-contain"
                />
                {activeCards.length}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Sheet lateral para combinar productos. Abre DIRECTO al grid de
          productos cargados (sin subir/elegir), con la selección actual
          tildada y el principal marcado. Sirve para una card o para el
          outfit de todo un producto. */}
      <MultiSlotProductPickerSheet
        open={sheetTarget !== null}
        onClose={() => setSheetTarget(null)}
        currentSelection={sheetSelection}
        onConfirm={handleSheetConfirm}
        userCompanyId={userCompanyId}
        brandId={brandId}
        rubro={rubro}
        tiposProductoSugeridos={tiposProductoSugeridos}
        onProductsRefreshed={onProductsRefreshed}
        directToPick
        initialSelection={sheetSelection}
        showPrimary
        title={
          sheetTarget?.type === 'product'
            ? 'Editar outfit'
            : 'Combinar productos'
        }
        pickDescription="Armá el outfit con el que se va a mostrar el producto principal: sumá las prendas o accesorios que querés que lleve en la misma imagen."
      />
    </div>
  );
};
